/** * 個人設定 */
<template>
  <div class="member-preference">
    <ifield-b-modal
      id="member-preference-dialog"
      ref="member-preference-dialog-modal"
      v-bind:title="$t('theMemberPreference.memberPreference')"
      centered
      scrollable
      no-body-border
      size="lg"
      ok-variant="success"
      ignore-enforce-focus-selector=".popover"
      v-bind:ok-title="$t('theMemberPreference.action.save')"
      v-bind:cancel-title="$t('theMemberPreference.action.cancel')"
      v-on:shown="$_onShownModal"
      v-on:ok.prevent="$_saveSettings"
      v-on:hide="$_onHideModal"
    >
      <loading-mask isFullScreen v-bind:isLoading="isSaving" />
      <form-group
        id="member-preference-form"
        ref="formGroup"
        v-bind:mapTypes="$store.state.login.mapTypes"
      >
        <!-- 地図ホームポジション地図 -->
        <template
          v-slot:[`${homePositionFieldName}-map`]="{ selectStart, onSelectEnd }"
        >
          <map-control
            v-bind="$store.getters.mapControlBinds"
            v-bind:id-prefix="'member-preference-'"
            v-bind:height="'80vh'"
            v-bind:can-change-indoor-level="false"
            v-bind:onIfieldApiError="handleApiError"
            v-on:map-control-initend="selectStart"
            v-on:map-control-layer-selectend="onSelectEnd"
            v-on:menu-layers-visible-changed="
              $store.dispatch('map/setVisible', $event)
            "
          />
        </template>
        <!-- マップ回転角度反映ボタン（地図のホームポジション角度） -->
        <template
          v-slot:[`${homePositionHeadingFieldName}-append`]="{
            idPrefix,
            setValue,
          }"
        >
          <b-button
            v-bind:id="`${idPrefix}-heading-apply-button`"
            class="mt-1"
            size="sm"
            variant="success"
            v-on:click="() => setValue(normalizedHeading)"
          >
            {{ $t('theMemberPreference.action.applyCurrentHeading') }}
          </b-button>
        </template>
        <!--
          スロット挿入(ATTRIBUTE_TYPE.SLOT)の設定項目
          「アイコンをクラスター表示する」のように、1つの設定項目の中に複数のトグルスイッチを
          並べる項目は特殊な構造を持つためメインのフォームグループ(formGroup)のスロットを利用し、
          その中に項目ごとのフォームグループを入れ子で配置している。
          これは、個々のトグルスイッチの状態を親コンポーネント(TheMemberPreference)で一元管理し、
          バリデーションや値の保存処理を他のフォーム項目と分離して独自に実装するため。
          項目キーを名指しせず選択肢(select_values)からトグルスイッチを組み立てるので、
          同じ形の設定項目がマスタに増えても画面側に手を入れる必要は無い。
        -->
        <template v-for="slotField in slotFields" v-slot:[slotField.fieldKey]>
          <form-group
            v-bind:key="slotField.fieldKey"
            v-bind:id="`member-preference-${slotField.fieldKey}-form`"
            class="slot-toggles-group"
            v-bind:ref="`slotFormGroup-${slotField.fieldKey}`"
            v-on:form-group-value-change="
              (fieldName, value) =>
                $_onSlotToggleChange(slotField.fieldKey, fieldName, value)
            "
          />
        </template>
      </form-group>
    </ifield-b-modal>
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { mapState, mapGetters, mapActions } from 'vuex'
import { FUNCTION_TYPE } from '@/constants.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useMapIndoorStore } from '@/composables/map/UseMapIndoorStore'

// 「位置通知判定方式」の item_key
const LOCATION_SEND_AUTO_ON_OFF_KEY = 'location_send_auto_on_off'

/**
 * 設定項目情報からFormGroupの項目データを作成する
 */
class FormField {
  /**
   * コンストラクタ
   * @param {Object} item 設定項目情報(/api/user_setting/myaccount APIのdataの配列要素)
   */
  constructor(item) {
    this.item = item
    this.itemType = Number(item.item_type)
  }

  /**
   * 項目キー
   * @return {string}
   */
  get fieldKey() {
    return this.item.item_key
  }

  /**
   * 設定取得APIのレスポンスデータからFormGroupに設定するカスタム項目データを作成する
   * @param {Object} item 設定項目情報(/api/user_setting/myaccount APIのdataの配列要素)
   */
  getFormField() {
    const item = this.item
    return {
      fieldName: this.fieldKey, // 保存時にIDが必要なため、keyではなくIDを設定する
      label: item.item_name,
      attributeType: this.itemType,
      value: this.getFormValue(),
      selectValues: this.getSelectValues(),
      validateRule: this.getValidateRule(),
      ...this.getFormOption(),
    }
  }

  /**
   * フォームの値を取得する
   * @returns {Object} フォームの値
   */
  getFormValue() {
    const itemValue = this.item.item_value

    if (!itemValue) {
      return null
    }

    let value
    switch (this.itemType) {
      case ATTRIBUTE_TYPE.NUMBER: {
        // 数値
        value = Number(itemValue)
        break
      }
      case ATTRIBUTE_TYPE.CHECK: {
        // チェック
        value = itemValue.split('|')
        break
      }
      case ATTRIBUTE_TYPE.MULTI_SELECT: {
        // 複数選択リスト
        value = itemValue.split('|')
        break
      }
      case ATTRIBUTE_TYPE.TOGGLE: {
        // トグル
        // select_valuesの先頭のcodeと一致していたらtrue
        value = itemValue === this.item.select_values[0].item_code
        break
      }
      case ATTRIBUTE_TYPE.LOCATION: {
        // ロケーション
        const coordinates = itemValue
          .split('|')
          .map((value) => new Number(value))
        if (coordinates.length === 2 && coordinates[0] && coordinates[1]) {
          // item_valueは"lat|lng"だが、AppFormLocationでは[lng, lat]で渡す
          value = [Number(coordinates[1]), Number(coordinates[0])]
        }
        break
      }
      default: {
        value = itemValue
        break
      }
    }
    return value
  }

  /**
   * フォームの選択リストを取得する
   * @returns {Array} 選択リスト、ただし選択リストを持たない場合はnull
   */
  getSelectValues() {
    const item = this.item
    const itemType = this.itemType
    let selectValues
    if (
      itemType === ATTRIBUTE_TYPE.SINGLE_SELECT ||
      itemType === ATTRIBUTE_TYPE.MULTI_SELECT ||
      itemType === ATTRIBUTE_TYPE.CHECK
    ) {
      // 選択リスト、チェックの場合
      selectValues = item.select_values.map((selectValue) => {
        return { value: selectValue.item_code, text: selectValue.item_name }
      })
    } else if (itemType === ATTRIBUTE_TYPE.TOGGLE) {
      // トグルの場合は選択リストの先頭の値をtrueとする
      selectValues = [
        { value: true, text: item.select_values[0].item_name },
        { value: false, text: item.select_values[1].item_name },
      ]
    } else if (itemType === ATTRIBUTE_TYPE.SLOT) {
      selectValues = item.select_values.map((selectValue) => {
        return { value: selectValue.item_code, text: selectValue.item_name }
      })
    } else {
      selectValues = null
    }
    return selectValues
  }

  /**
   * バリデーションルールを取得する
   * @returns {Object} バリデーションルール
   */
  getValidateRule() {
    const item = this.item
    const itemType = Number(item.item_type)

    // スロット挿入の項目は「全てのトグルがOFF」という状態が許容され、
    // その場合APIへの送信値は空文字""となるためform-groupの標準的なバリデーションから除外
    // ここではルールを空にしてバリデーションをスキップし、値の妥当性チェックは
    // 親コンポーネント側(TheMemberPreference)のロジックに委ねる。
    if (itemType === ATTRIBUTE_TYPE.SLOT) return {}

    let validateRule = {
      required: true,
    }
    if (itemType === ATTRIBUTE_TYPE.NUMBER) {
      // 数値型の場合、最小最大値をを設定する
      // 最小値を設定する
      if (item.item_min_value) {
        validateRule['min_value'] = Number(item.item_min_value)
      }
      // 最大値を設定する
      if (item.item_max_value) {
        validateRule['max_value'] = Number(item.item_max_value)
      }
    }
    return validateRule
  }

  /**
   * フォーム特有のオプションを取得する
   * @returns {Object} オプション
   */
  getFormOption() {
    switch (this.itemType) {
      case ATTRIBUTE_TYPE.LOCATION:
        return { modalSize: 'xl' }
      default:
        return {}
    }
  }

  /**
   * FormGroupのに設定された値からサーバーに保存する(API POST用)を作成する
   * @param {Object} formValue フォームに入力されている値
   */
  makeSettingItem(formValue) {
    // 値が設定されていない場合はnullとする
    let itemValue
    if (formValue !== null) {
      switch (this.itemType) {
        case ATTRIBUTE_TYPE.CHECK: {
          itemValue = formValue.join('|')
          break
        }
        case ATTRIBUTE_TYPE.TOGGLE: {
          // トグルの場合
          // item.select_valuesの先頭要素がtrue
          const selectValues = this.item.select_values
          itemValue = formValue
            ? selectValues[0].item_code
            : selectValues[1].item_code
          break
        }
        case ATTRIBUTE_TYPE.MULTI_SELECT: {
          itemValue = formValue.join('|')
          break
        }
        case ATTRIBUTE_TYPE.LOCATION: {
          // ロケーションの場合、"lat|lng"
          itemValue = [formValue.coordinates[1], formValue.coordinates[0]].join(
            '|'
          )
          break
        }
        default: {
          itemValue = formValue
          break
        }
      }
    } else {
      itemValue = null
    }

    return {
      id: this.item.id,
      item_value: itemValue,
    }
  }
}

/**
 * 設定項目情報からFormGroupの項目データを作成する(位置通知判定方式)
 */
class FormFieldLoactionSend extends FormField {
  static ITEM_CODE = {
    PERIOD: '1',
    TASK: '2',
    INDOOR_SITE: '3',
  }

  /**
   * 設定取得APIのレスポンスデータからFormGroupに設定するカスタム項目データを作成する
   * @param {{ isGpsAvaliable: boolean; isScheduleAvailable: boolean; }} options
   */
  getFormField(options) {
    if (!options.isGpsAvaliable) return null

    const item = this.item
    return {
      fieldName: this.fieldKey, // 保存時にIDが必要なため、keyではなくIDを設定する
      label: item.item_name,
      attributeType: this.itemType,
      value: this.getFormValue(),
      selectValues: this.getSelectValues(options.isScheduleAvailable),
      ...this.getFormOption(),
    }
  }

  /**
   * フォームの選択リストを取得する
   * @param {boolean} isScheduleAvailable スケジュールの利用権限がある場合はtrue
   * @returns {Array} 選択リスト、ただし選択リストを持たない場合はnull
   */
  getSelectValues(isScheduleAvailable) {
    const item = this.item
    return item.select_values
      .filter((selectValue) => {
        if (selectValue.item_code === FormFieldLoactionSend.ITEM_CODE.TASK) {
          return isScheduleAvailable
        } else {
          return true
        }
      })
      .map((selectValue) => ({
        value: selectValue.item_code,
        text: selectValue.item_name,
      }))
  }
}

export default {
  name: 'TheMemberPreference',
  setup(props, context) {
    const { handleApiError, showApiError } = useHandleApiError(props, context)
    const { changeIndoorBuilding, unselectedBuilding, setActiveIndoorLevelId } =
      useMapIndoorStore()
    return {
      handleApiError,
      showApiError,
      changeIndoorBuilding,
      unselectedBuilding,
      setActiveIndoorLevelId,
    }
  },
  data: function () {
    return {
      /**
       * フォームグループに設定する項目情報
       * @type {Array.<FormField>}
       */
      formFields: [],
      // 表示前の元屋内建物
      originalIndoorBuildings: [],
      // 表示前の元屋内レベルID
      originalActiveIndoorLevelId: null,
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * 保存中の場合はtrue
       * @type {Boolean}
       */
      isSaving: false,
      /**
       * スロット挿入の設定項目のトグル値
       * 項目キーごとに、個々のトグルのON/OFF状態を item_value をキーにして
       * { map_clustering_targets: { user: true, report: false, ... } } の形式で保持する。
       * @type {Object.<string, Object.<string, boolean>>}
       */
      slotToggleValues: {},
    }
  },
  computed: {
    ...mapGetters('mapIndoor', ['activeIndoorLevelId']),
    ...mapState('mapIndoor', {
      activeIndoorBuildings: (state) => state.indoorBuildings,
    }),
    /**
     * 定期通知が利用可能か？
     * @type {Boolean}
     */
    isGpsAvaliable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.GPS)
    },
    /**
     * スケジュールが利用可能か？
     * @type {Boolean}
     */
    isScheduleAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SCHEDULE)
    },
    /**
     * フォームグループデータ
     * @type {Array.Object}
     */
    formGroupFields: function () {
      const options = {
        isGpsAvaliable: this.isGpsAvaliable,
        isScheduleAvailable: this.isScheduleAvailable,
      }
      return this.formFields
        .map((formField) => formField.getFormField(options))
        .filter((v) => v)
    },
    /**
     * ホームポジションフィールドのフィールド名
     * @type {String}
     */
    homePositionFieldName: function () {
      const field = this.formGroupFields.find(
        (field) => field.attributeType === ATTRIBUTE_TYPE.LOCATION
      )
      return field ? field.fieldName : ''
    },
    /**
     * 地図のホームポジション角度フィールドのフィールド名
     * @type {String}
     */
    homePositionHeadingFieldName: function () {
      const field = this.formGroupFields.find(
        (field) => field.attributeType === ATTRIBUTE_TYPE.NUMBER
      )
      return field ? field.fieldName : ''
    },
    /**
     * 正規化済み heading
     * @type {Number}
     */
    normalizedHeading: function () {
      const heading = this.$store.getters['map/heading']
      // 0 - 360に正規化すること
      return ((heading % 360) + 360) % 360
    },
    /**
     * スロット挿入の設定項目の一覧
     * formGroupFields は getFormField を通した後のデータで item.select_values が加工済みのため、
     * ラベルや選択肢など、APIから取得した生の定義情報を参照できるように item をそのまま持たせる。
     * @type {Array.<{ fieldKey: String, item: Object }>}
     */
    slotFields: function () {
      return this.formFields
        .filter((formField) => formField.itemType === ATTRIBUTE_TYPE.SLOT)
        .map((formField) => ({
          fieldKey: formField.fieldKey,
          item: formField.item,
        }))
    },
  },
  mounted: function () {
    this.$log.info('TheMemberPreference mounted.')
  },
  methods: {
    ...mapActions({
      $_saveUserSettingToStore: 'saveUserSetting',
    }),

    /**
     * スロット挿入の設定項目のトグルの定義情報を作成する
     * 入れ子のフォームに渡すためのもので、選択肢(select_values)の1件が1つのトグルになる。
     * @param {{ fieldKey: String, item: Object }} slotField スロット挿入の設定項目
     * @returns {Array.<Object>} トグルの定義情報
     */
    $_makeSlotToggleFields: function (slotField) {
      const toggleValues = this.slotToggleValues[slotField.fieldKey] || {}
      return (slotField.item.select_values || []).map((selectValue) => ({
        // 項目キーを前置して、複数のスロット項目でフィールド名が衝突しないようにする
        fieldName: `${slotField.fieldKey}_${selectValue.item_code}`,
        label: selectValue.item_name,
        attributeType: ATTRIBUTE_TYPE.TOGGLE,
        // item_codeではなく、対応するitem_valueをキーとして値を取得する
        value: !!toggleValues[selectValue.item_value],
        selectValues: [
          { value: true, text: this.$t('theMemberPreference.value.yes') },
          { value: false, text: this.$t('theMemberPreference.value.no') },
        ],
      }))
    },

    /**
     * スロット挿入の設定項目のトグルの変更イベントハンドラ
     * 入れ子のフォーム内のトグルが変更された際に呼び出される。
     * 変更されたトグルの情報(fieldName, value)を受け取り、
     * このコンポーネントが管理する中央集権的な状態オブジェクト(slotToggleValues)を更新する。
     * @param {String} itemKey 設定項目のキー
     * @param {String} fieldName フィールド名
     * @param {boolean} value 変更された選択値
     */
    $_onSlotToggleChange: function (itemKey, fieldName, value) {
      const slotField = this.slotFields.find(
        (field) => field.fieldKey === itemKey
      )
      if (!slotField) return

      // プレフィックスを除去して元のキーとして値を更新する
      const itemCode = fieldName.replace(`${itemKey}_`, '')
      // item_code から対応する item_value を見つける
      const selectValue = (slotField.item.select_values || []).find(
        (sv) => sv.item_code === itemCode
      )
      if (selectValue) {
        // keyの変更をリアクティブに反映する
        this.$set(this.slotToggleValues[itemKey], selectValue.item_value, value)
      }
    },
    /**
     * ダイアログを表示する
     */
    showModal: async function () {
      this.$_showInitialLoading()
      try {
        await this.$_getSettings()
      } catch {
        this.$_hideInitialLoading()
        return
      }

      this.$_saveMapIndoorState()
      this.$refs['member-preference-dialog-modal'].show()
      await this.$nextTick()
      // 1. 親フォーム(formGroup)描画
      this.$refs['formGroup'].setData(this.formGroupFields)
      // 親フォームグループの描画を待つ
      await this.$nextTick()
      // 2. 次に、スロット内の子フォームを項目ごとに描画
      this.slotFields.forEach((slotField) => {
        // v-for の中の ref は配列になる
        const formGroups = this.$refs[`slotFormGroup-${slotField.fieldKey}`]
        const formGroup = Array.isArray(formGroups) ? formGroups[0] : formGroups
        if (formGroup) {
          formGroup.setData(this.$_makeSlotToggleFields(slotField))
        }
      })
      this.$_hideInitialLoading()
    },

    /**
     * ダイアログを非表示にする
     */
    $_hideModal: function () {
      this.$refs['member-preference-dialog-modal'].hide()
    },

    /**
     * 個人設定データを取得する
     */
    $_getSettings: async function () {
      const url = RestApiClient.createApiUrl('user_setting/myaccount')
      try {
        const json = await RestApiClient.executeGetApi(url)
        // 有効な設定のみを画面に表示する
        const settings = json.data.filter((d) => d.enable)

        // スロット挿入の設定項目のトグルの初期値を設定
        this.slotToggleValues = settings
          .filter((s) => Number(s.item_type) === ATTRIBUTE_TYPE.SLOT)
          .reduce((toggleValues, setting) => {
            toggleValues[setting.item_key] =
              this.$_getSlotToggleInitialValue(setting)
            return toggleValues
          }, {})

        // フォームグループデータを作成
        this.formFields = settings.map((setting) =>
          setting.item_key === LOCATION_SEND_AUTO_ON_OFF_KEY
            ? new FormFieldLoactionSend(setting)
            : new FormField(setting)
        )
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * スロット挿入の設定項目のトグルの初期値を生成
     * APIレスポンスを元に、item_value をキーにしたON/OFFのオブジェクトを作る。
     * @param {string} item_value 設定値 (例: "1|4")
     * @param {Array} select_values 選択肢定義
     * @returns {Object.<string, boolean>} トグルの初期値 (例: { user: true, report: false })
     */
    $_getSlotToggleInitialValue: function ({ item_value, select_values }) {
      // サーバに保存されている設定値を分解（'1|2|3|4' 形式）
      const selectedCodes = item_value
        ? item_value.split('|').filter(Boolean)
        : []

      // トグルフォーム用の初期選択値を、選択肢の item_value をキーにして生成
      // （例： { 'user': true, 'report': false ... }）
      return (select_values || []).reduce((initialValues, selectValue) => {
        initialValues[selectValue.item_value] = selectedCodes.includes(
          selectValue.item_code
        )
        return initialValues
      }, {})
    },

    /**
     * 利用可能な設定項目を取得する
     * @param {Array.<Object>} data 全設定項目データ
     * @returns {Array.<Object>} 利用可能な設定項目データ
     */
    $_getEnableSettings: function (data) {
      return data.filter((d) => {
        if (d.item_key === LOCATION_SEND_AUTO_ON_OFF_KEY) {
          // 「位置通知判定方式」はスケジュールの権限があるときだけ有効
          return d.enable && this.isScheduleAvailable
        } else {
          return d.enable
        }
      })
    },

    /**
     * モーダル表示前の屋内状態を保存する
     */
    $_saveMapIndoorState: function () {
      this.originalIndoorBuildings = [...this.activeIndoorBuildings]
      this.originalActiveIndoorLevelId = this.activeIndoorLevelId
    },

    /**
     * モーダル表示完了時処理
     */
    $_onShownModal: function () {
      // 背後の地図描画に反映させないためにモーダル表示完了してから屋外に出る
      this.$nextTick().then(() => {
        this.unselectedBuilding()
      })
    },

    /**
     * 個人設定を保存する
     */
    $_saveSettings: async function () {
      // ローディングを表示
      this.$_showSaveLoading()
      // フォームグループの値を取得する
      let formGroupValues
      try {
        formGroupValues = this.$refs['formGroup'].getValues()
      } catch {
        // FromGroupがエラーダイアログが表示しているのでここでは何もしない
        this.$_hideSaveLoading()
        return null
      }

      // 保存用の個人設定を作成
      const saveSettings = this.formFields.map((formField) => {
        const formValue = formGroupValues.find((value) => {
          return value.name === formField.fieldKey
        })
        let value = formValue ? formValue.value : null

        // スロット挿入の設定項目は特殊な値の取得方法を行う。
        // formGroup.getValues() から返される値はバリデーションをスキップしているため信頼できない。
        // 代わりに、このコンポーネントが直接管理している状態オブジェクト(slotToggleValues)を参照する。
        if (formField.itemType === ATTRIBUTE_TYPE.SLOT) {
          // 1. slotToggleValues から true になっているキー (例: 'user', 'checkin') の配列を生成する。
          const toggleValues = this.slotToggleValues[formField.fieldKey] || {}
          const selectedKeys = Object.entries(toggleValues)
            .filter(([, checked]) => checked)
            .map(([key]) => key)
          // 2. キーの配列を、APIが要求する item_code を'|'で連結した文字列 (例: "1|4") に変換する。
          //    全てOFFの場合は空文字""となる。
          value = (formField.item.select_values || [])
            .filter((sv) => selectedKeys.includes(sv.item_value))
            .map((sv) => sv.item_code)
            .join('|')
        }

        return formField.makeSettingItem(value)
      })

      // サーバーに保存する
      try {
        const url = RestApiClient.createApiUrl('user_setting/myaccount')
        // 保存APIを実行する
        const saveResult = await RestApiClient.executePostApi(url, saveSettings)
        this.$log.debug('API Response body:', url.pathname, saveResult)

        // 最新の設定をサーバーから取得してstoreに保存する
        const settingJson = await RestApiClient.executeGetApi(url)
        this.$_saveUserSettingToStore(settingJson.data)

        // 個人設定の更新時のみ、軌跡表示フラグを上書き
        const setting = this.$store.state.userHistory.historyLineFlg
        this.$store.commit(
          'analyzeTrack/setShowLine',
          setting.value === setting.selectValues[0]
        )

        this.$_showSaveSuccessMessage()
        this.$_hideModal()

        // イベントを発生させる
        this.$emit('member-preference-setend')
      } catch (error) {
        this.handleApiError(error, this.$_showSaveFailure)
      } finally {
        this.$_hideSaveLoading()
      }
    },

    /**
     * 設定の保存が成功したメッセージを表示する
     */
    $_showSaveSuccessMessage: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.$t('theMemberPreference.message.saved'),
        showConfirmButton: true,
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * 設定の保存APIで発生したエラーメッセージを表示する
     * @param {Object} responseJson APIのレスポンス(JSON)
     */
    $_createSaveFailureMessage: function (responseJson) {
      let messages = []
      // 項目ごとのエラーが設定されている場合
      // 項目ごとにエラーを表示する
      if (responseJson.errors) {
        responseJson.errors.forEach((error) => {
          // TODO error.field に対応した画面上の項目名(表示名)をメッセージに含めるようにする
          const message = error.message
          messages.push(message)
        })
      } else {
        // 項目ごとのエラーが設定されていない場合は、エラーメッセージを表示する
        messages.push(responseJson.message)
      }

      return messages.join('<br/>')
    },

    /**
     * 設定の保存APIで発生したエラーを表示する
     * @param {Object} error fetch実行時に発生したエラー
     */
    $_showSaveFailure: async function (error) {
      if (error instanceof Response) {
        // TODO:response.jsonが取れないときの処理
        const json = await error.json()
        const message = json
          ? this.$_createSaveFailureMessage(json)
          : this.$t('theMemberPreference.message.serverErrorStatus', {
              code: error.status,
            })
        this.showApiError(new Error(message))
      } else {
        this.showApiError(error)
      }
    },

    /**
     * モーダルが閉じるときの処理
     */
    $_onHideModal: function () {
      this.$_restoreMapIndoorState()
      this.originalIndoorBuildings = []
      this.originalActiveIndoorLevelId = null
    },

    /**
     * モーダル表示前の屋内状態を復元する
     */
    $_restoreMapIndoorState: function () {
      this.setActiveIndoorLevelId(this.originalActiveIndoorLevelId)
      this.changeIndoorBuilding(this.originalIndoorBuildings[0])
    },

    /**
     * 初期表示時のローディングを表示する
     */
    $_showInitialLoading: function () {
      this.isLoading = true
    },

    /**
     * 初期表示時のローディングを非表示にする
     */
    $_hideInitialLoading: function () {
      this.isLoading = false
    },

    /**
     * 保存時のローディングを表示する
     */
    $_showSaveLoading: function () {
      this.isSaving = true
    },

    /**
     * 保存時のローディングを非表示にする
     */
    $_hideSaveLoading: function () {
      this.isSaving = false
    },
  },
}
</script>

<style lang="scss" scoped>
// form-slot内の必須バッジと入力欄の垂直位置を揃えるためのスタイル
::v-deep {
  .form-slot {
    .app-form-group {
      padding: 0;
    }
    // スロット挿入の設定項目のトグルを2列に並べるためのスタイル
    .slot-toggles-group {
      .form-group-outer {
        > span {
          display: flex;
          flex-wrap: wrap;
          .each-form {
            width: 50%;
            min-height: 0;
            border-bottom: none;
            padding-bottom: 0;
            .row.no-gutters {
              .required-badge-area {
                display: none;
              }
            }
            // legend要素の幅を固定
            .form-toggle .form-row > legend {
              flex: 0 0 100px;
              max-width: 100px;
            }
          }
          .form-group {
            margin-bottom: 0;
          }
        }
      }
    }
  }
}
</style>
